// Phase 8 — Login screen, migrated to shadcn/Tailwind.
// Phase 9 — Email + Password replaced by Email + OTP (5-minute validity).
//
// The password-based flow is kept, fully commented out, at the bottom of this
// file for rollback — see LEGACY block. Do not delete it.
//
// UI only: a split-screen layout — an editorial fashion showcase on one side and a
// sleek glassmorphism login form (floating labels) on the other, now driven by a
// two-step email -> OTP flow instead of email/password.
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { AlertCircle } from "lucide-react"
import AppConstant from "../Globals/AppConstants"
import AppImages from "../Globals/AppImages"
import { sendLoginOtpAction, verifyLoginOtpAction, clearLoginOtpStateAction } from "../Store/actions/apparelAction"
import { Button } from "ui/button"
import { Input } from "ui/input"

// Editorial modelling showcase for the left panel. A brand gradient sits behind it,
// so if the image ever fails to load the panel still renders on-brand.
const EDITORIAL_IMAGE =
    "https://images.unsplash.com/photo-1445205170230-053b83016050?auto=format&fit=crop&w=1400&q=90"

const OTP_VALIDITY_SECONDS = AppConstant?.OTP_VALIDITY_SECONDS ?? 300;
const OTP_LENGTH = 6;

const formatCountdown = (totalSeconds: number) => {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
}

const ApparelLogin: any = () => {
    const dispatch = useDispatch();

    const [step, setStep] = useState<"email" | "otp">("email");
    const [email, setEmail] = useState("");
    const [otpDigits, setOtpDigits] = useState<string[]>(Array(OTP_LENGTH).fill(""));
    const [touched, setTouched] = useState({ email: false, otp: false });
    const [errorMessage, setErrorMessage] = useState("");
    const [showMessage, setShowMessage] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(0);

    const otp = otpDigits.join("");
    const setOtp = (value: string) => {
        const digits = value.replace(/\D/g, "").slice(0, OTP_LENGTH).split("");
        setOtpDigits(Array.from({ length: OTP_LENGTH }, (_, i) => digits[i] || ""));
    }

    const otpRequestedRef = useRef(false);
    const verifyRequestedRef = useRef(false);
    const expiryRef = useRef<number | null>(null);
    const otpInputRefs = useRef<Array<HTMLInputElement | null>>([]);

    const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
    const { sendLoginOtpOnload, sendLoginOtpData, verifyLoginOtpOnload, verifyLoginOtpData } = apparelReducerState;

    const emailInvalid = email.length === 0;
    const otpInvalid = otp.length === 0;

    // Countdown ticks off a fixed expiry timestamp (not a naive decrement) so it
    // stays correct even if the tab is backgrounded and timers get throttled.
    useEffect(() => {
        if (step !== "otp" || !expiryRef.current) return;
        const tick = () => {
            const remaining = Math.max(0, Math.round(((expiryRef.current as number) - Date.now()) / 1000));
            setSecondsLeft(remaining);
        };
        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [step, expiryRef.current])

    // Auto-hide the error banner after 5s (legacy behaviour, kept).
    useEffect(() => {
        if (showMessage) {
            const t = setTimeout(() => setShowMessage(false), 5000);
            return () => clearTimeout(t);
        }
    }, [showMessage])

    // Land the cursor in the first OTP box the moment the step switches in —
    // otherwise the user has to click before they can start typing the code.
    useEffect(() => {
        if (step === "otp") {
            otpInputRefs.current[0]?.focus();
        }
    }, [step])

    const requestOtp = () => {
        setErrorMessage("");
        setShowMessage(false);
        dispatch(sendLoginOtpAction({ email }));
        otpRequestedRef.current = true;
    }

    useEffect(() => {
        if (otpRequestedRef.current && !sendLoginOtpOnload) {
            otpRequestedRef.current = false;
            const response = sendLoginOtpData?.requestLoginOtp;
            if (response?.success === false) {
                setErrorMessage(response?.message || AppConstant?.somethingWentWrong);
                setShowMessage(true);
                return;
            }
            expiryRef.current = Date.now() + OTP_VALIDITY_SECONDS * 1000;
            setSecondsLeft(OTP_VALIDITY_SECONDS);
            setOtp("");
            setTouched((t) => ({ ...t, otp: false }));
            setStep("otp");
            // Covers resend too (step is already "otp" then, so the
            // step-keyed focus effect alone wouldn't re-fire).
            otpInputRefs.current[0]?.focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sendLoginOtpOnload])

    const verifyOtp = () => {
        setErrorMessage("");
        setShowMessage(false);
        dispatch(verifyLoginOtpAction({ email, otp }));
        verifyRequestedRef.current = true;
    }

    useEffect(() => {
        if (verifyRequestedRef.current && !verifyLoginOtpOnload) {
            verifyRequestedRef.current = false;
            const login = verifyLoginOtpData?.login;
            if (!login?.user) {
                setErrorMessage(login?.message || AppConstant?.invalidOtp);
                setShowMessage(true);
                setOtp("");
                setTouched((t) => ({ ...t, otp: false }));
                otpInputRefs.current[0]?.focus();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [verifyLoginOtpOnload])

    // Auto-submit the moment all 6 boxes are filled — the user shouldn't have
    // to also click "Verify OTP" once the code is complete. A failed attempt
    // clears otpDigits (above), which drops otp.length back below OTP_LENGTH,
    // so this won't re-fire until a fresh full code is entered.
    useEffect(() => {
        if (step === "otp" && otp.length === OTP_LENGTH && secondsLeft > 0 && !verifyLoginOtpOnload && !verifyRequestedRef.current) {
            verifyOtp();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [otp, step])

    const handleChangeEmail = () => {
        dispatch(clearLoginOtpStateAction());
        expiryRef.current = null;
        setSecondsLeft(0);
        setOtp("");
        setErrorMessage("");
        setShowMessage(false);
        setStep("email");
    }

    const handleEmailSubmit = (e: any) => {
        e.preventDefault();
        if (emailInvalid) {
            setTouched((t) => ({ ...t, email: true }));
            return;
        }
        requestOtp();
    }

    const handleOtpSubmit = (e: any) => {
        e.preventDefault();
        if (otpInvalid || secondsLeft <= 0) {
            setTouched((t) => ({ ...t, otp: true }));
            return;
        }
        verifyOtp();
    }

    // Each OTP box holds a single digit; typing advances focus, backspace on an
    // empty box steps back, and pasting a full code fills every box at once.
    const handleOtpDigitChange = (index: number, rawValue: string) => {
        const digits = rawValue.replace(/\D/g, "");
        if (!digits) {
            setOtpDigits((prev) => prev.map((d, i) => (i === index ? "" : d)));
            return;
        }
        setOtpDigits((prev) => {
            const next = [...prev];
            let i = index;
            for (const digit of digits) {
                if (i >= OTP_LENGTH) break;
                next[i] = digit;
                i++;
            }
            return next;
        });
        otpInputRefs.current[Math.min(index + digits.length, OTP_LENGTH - 1)]?.focus();
    }

    const handleOtpKeyDown = (index: number, e: any) => {
        if (e.key === "Backspace") {
            if (otpDigits[index]) {
                setOtpDigits((prev) => prev.map((d, i) => (i === index ? "" : d)));
            } else if (index > 0) {
                setOtpDigits((prev) => prev.map((d, i) => (i === index - 1 ? "" : d)));
                otpInputRefs.current[index - 1]?.focus();
            }
        } else if (e.key === "ArrowLeft" && index > 0) {
            otpInputRefs.current[index - 1]?.focus();
        } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
            otpInputRefs.current[index + 1]?.focus();
        }
    }

    const handleOtpPaste = (e: any) => {
        e.preventDefault();
        const pasted = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, OTP_LENGTH);
        if (!pasted) return;
        setOtp(pasted);
        otpInputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
    }

    const otpExpired = step === "otp" && secondsLeft <= 0;

    return (
        <div className="mz flex min-h-screen w-full bg-background">
            {/* ── Editorial showcase ─────────────────────────────────────────── */}
            <div className="relative hidden overflow-hidden lg:flex lg:w-1/2 xl:w-[58%]">
                {/* brand gradient base (visible even if the photo fails to load) */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/80 to-secondary" />
                <img
                    src={EDITORIAL_IMAGE}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                {/* legibility + brand wash over the photo */}
                <div className="absolute inset-0 bg-gradient-to-tr from-primary/60 via-primary/10 to-secondary/40 mix-blend-multiply" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/25" />

                <div className="relative z-10 flex w-full flex-col justify-between p-10 xl:p-14">
                    <div className="inline-flex w-fit items-center rounded-xl bg-white/90 px-4 py-2.5 shadow-lg backdrop-blur">
                        <img src={AppImages?.bigLogo} alt="Mukizh Fashions" className="h-8 object-contain" />
                    </div>

                    <div className="max-w-lg">
                        <p className="mb-4 text-xs font-medium uppercase tracking-[0.3em] text-white/70">
                            Mukizh · Fashion Retail
                        </p>
                        <h2 className="text-4xl font-semibold leading-[1.15] text-white xl:text-5xl">
                            Where every thread<br />tells your story.
                        </h2>
                        <p className="mt-5 max-w-md text-base leading-relaxed text-white/80">
                            Curated collections for men, women &amp; kids — inventory, billing and
                            storefront, managed beautifully in one place.
                        </p>
                    </div>
                </div>
            </div>

            {/* ── Login form ─────────────────────────────────────────────────── */}
            <div className="flex w-full items-center justify-center bg-muted/40 px-6 py-12 lg:w-1/2 xl:w-[42%]">
                <div className="w-full max-w-md">
                    {/* logo for small screens (the editorial panel is hidden there) */}
                    <div className="mb-8 flex justify-center lg:hidden">
                        <img src={AppImages?.bigLogo} alt="Mukizh Fashions" className="h-12 object-contain" />
                    </div>

                    <div className="rounded-2xl border border-border/60 bg-card/70 p-8 shadow-xl backdrop-blur-xl sm:p-10">
                        <h1 className="text-2xl font-semibold text-foreground">Welcome back</h1>
                        <p className="mt-1.5 text-sm text-muted-foreground">
                            {step === "email"
                                ? "Sign in to your Mukizh account to continue."
                                : `${AppConstant?.otpSentTo} ${email}`}
                        </p>

                        {step === "email" ? (
                            <form onSubmit={handleEmailSubmit} className="mt-8 space-y-5">
                                {/* Email — floating label */}
                                <div>
                                    <div className="relative">
                                        <Input
                                            id="login-email"
                                            placeholder=" "
                                            autoComplete="off"
                                            onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                                            onChange={(e: any) => setEmail(e?.target?.value)}
                                            className="peer h-14 rounded-xl border-input/70 bg-background/60 px-4 pt-4 text-sm backdrop-blur placeholder:text-transparent focus-visible:ring-offset-0"
                                        />
                                        <label
                                            htmlFor="login-email"
                                            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-muted-foreground transition-all peer-focus:top-2.5 peer-focus:translate-y-0 peer-focus:text-xs peer-focus:font-medium peer-focus:text-primary peer-[:not(:placeholder-shown)]:top-2.5 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-xs"
                                        >
                                            {AppConstant?.emailId}
                                        </label>
                                    </div>
                                    {touched.email && emailInvalid && (
                                        <p className="mt-1.5 text-xs text-destructive">E-mail is required</p>
                                    )}
                                </div>

                                {showMessage == true && (
                                    <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive">
                                        <AlertCircle className="h-4 w-4 shrink-0" /> {errorMessage}
                                    </div>
                                )}

                                <Button type="submit" disabled={sendLoginOtpOnload} className="h-12 w-full rounded-xl text-sm font-medium">
                                    {sendLoginOtpOnload ? "Sending…" : AppConstant?.sendOtp}
                                </Button>
                            </form>
                        ) : (
                            <form onSubmit={handleOtpSubmit} className="mt-8 space-y-5">
                                {/* OTP — one input box per digit */}
                                <div>
                                    <div className="flex items-center justify-center gap-2 sm:gap-3" onPaste={handleOtpPaste}>
                                        {otpDigits.map((digit, index) => (
                                            <Input
                                                key={index}
                                                ref={(el: any) => (otpInputRefs.current[index] = el)}
                                                id={`login-otp-${index}`}
                                                type="text"
                                                inputMode="numeric"
                                                autoComplete={index === 0 ? "one-time-code" : "off"}
                                                maxLength={1}
                                                value={digit}
                                                onChange={(e: any) => handleOtpDigitChange(index, e?.target?.value)}
                                                onKeyDown={(e: any) => handleOtpKeyDown(index, e)}
                                                onBlur={() => setTouched((t) => ({ ...t, otp: true }))}
                                                className="h-14 w-11 rounded-xl border-input/70 bg-background/60 text-center text-lg font-semibold backdrop-blur focus-visible:ring-offset-0 sm:w-14"
                                            />
                                        ))}
                                    </div>
                                    {touched.otp && otpInvalid && (
                                        <p className="mt-1.5 text-center text-xs text-destructive">OTP is required</p>
                                    )}
                                </div>

                                <div className="flex items-center justify-between text-xs text-muted-foreground">
                                    <span>
                                        {otpExpired ? AppConstant?.otpExpired : `Expires in ${formatCountdown(secondsLeft)}`}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={requestOtp}
                                        disabled={sendLoginOtpOnload}
                                        className="font-medium text-primary hover:underline disabled:opacity-50"
                                    >
                                        {AppConstant?.resendOtp}
                                    </button>
                                </div>

                                {showMessage == true && (
                                    <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive">
                                        <AlertCircle className="h-4 w-4 shrink-0" /> {errorMessage}
                                    </div>
                                )}

                                <Button
                                    type="submit"
                                    disabled={verifyLoginOtpOnload || otpExpired}
                                    className="h-12 w-full rounded-xl text-sm font-medium"
                                >
                                    {verifyLoginOtpOnload ? "Verifying…" : AppConstant?.verifyOtp}
                                </Button>

                                {/* <button
                                    type="button"
                                    onClick={handleChangeEmail}
                                    className="w-full text-center text-sm font-medium text-muted-foreground hover:text-foreground hover:underline"
                                >
                                    {AppConstant?.changeEmail}
                                </button> */}
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ApparelLogin

/* ============================================================================
 * LEGACY — Email + Password login (Phase 8), kept for rollback.
 * Re-enable by restoring this as the default export and reverting the
 * import/action/saga/reducer wiring for API_APPAREL_LOGIN_* in:
 *   Store/actions/apparelAction.ts, Store/saga/apparelGraphQlQueries.ts,
 *   Store/saga/apparelSaga.ts, Store/saga/index.ts, Store/reducer/apparelReducer.ts
 * (that wiring was untouched by the OTP change, so no other rollback needed
 * there — only this component needs to be swapped back).
 * ============================================================================

import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { AlertCircle, Eye, EyeOff } from "lucide-react"
import AppConstant from "../Globals/AppConstants"
import AppImages from "../Globals/AppImages"
import { apparelLoginAction } from "../Store/actions/apparelAction"
import { deepCopyFunction, encryptValue } from "../Utils/helpers"
import { Button } from "ui/button"
import { Input } from "ui/input"

const EDITORIAL_IMAGE =
    "https://images.unsplash.com/photo-1445205170230-053b83016050?auto=format&fit=crop&w=1400&q=90"

const ApparelLoginPasswordFlow: any = () => {
    const dispatch = useDispatch();
    const apparelLoginTemp = {
        userName: "",
        password: "",
        showPassword: false
    }
    const [apparelLoginValue, setApparelLoginValue] = useState<any>(deepCopyFunction(apparelLoginTemp));
    const [touched, setTouched] = useState({ userName: false, password: false });
    const [apparelOnload, setApparelOnload] = useState(false);
    const [showMessage, setShowMessage] = useState(false);
    const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
    const { apparelLoginOnload, apparelLoginData } = apparelReducerState;
    const login = apparelLoginData?.login;

    const apparelLoginApi = () => {
        let emailAndPassword = apparelLoginValue?.userName + ":" + apparelLoginValue?.password;
        let payload = {
            credential: encryptValue(emailAndPassword, AppConstant?.SECRET_KEY)
        }
        dispatch(apparelLoginAction(payload));
        setApparelOnload(true)
    }

    useEffect(() => {
        if (apparelOnload && !apparelLoginOnload) {
            if (login?.includes("Unsuccessful")) {
                setShowMessage(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apparelOnload, apparelLoginOnload])

    useEffect(() => {
        if (showMessage) {
            const t = setTimeout(() => setShowMessage(false), 5000);
            return () => clearTimeout(t);
        }
    }, [showMessage])

    const handleClickShowPassword = () => {
        setApparelLoginValue({
            ...apparelLoginValue,
            showPassword: !apparelLoginValue.showPassword,
        });
    };

    const handleSubmit = (e: any) => {
        e.preventDefault();
        apparelLoginApi();
    }

    const emailInvalid = (apparelLoginValue?.userName).length == 0;
    const passwordInvalid = (apparelLoginValue?.password).length == 0;

    return (
        <div className="mz flex min-h-screen w-full bg-background">
            <div className="relative hidden overflow-hidden lg:flex lg:w-1/2 xl:w-[58%]">
                <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/80 to-secondary" />
                <img
                    src={EDITORIAL_IMAGE}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-primary/60 via-primary/10 to-secondary/40 mix-blend-multiply" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/25" />

                <div className="relative z-10 flex w-full flex-col justify-between p-10 xl:p-14">
                    <div className="inline-flex w-fit items-center rounded-xl bg-white/90 px-4 py-2.5 shadow-lg backdrop-blur">
                        <img src={AppImages?.bigLogo} alt="Mukizh Fashions" className="h-8 object-contain" />
                    </div>

                    <div className="max-w-lg">
                        <p className="mb-4 text-xs font-medium uppercase tracking-[0.3em] text-white/70">
                            Mukizh · Fashion Retail
                        </p>
                        <h2 className="text-4xl font-semibold leading-[1.15] text-white xl:text-5xl">
                            Where every thread<br />tells your story.
                        </h2>
                        <p className="mt-5 max-w-md text-base leading-relaxed text-white/80">
                            Curated collections for men, women &amp; kids — inventory, billing and
                            storefront, managed beautifully in one place.
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex w-full items-center justify-center bg-muted/40 px-6 py-12 lg:w-1/2 xl:w-[42%]">
                <div className="w-full max-w-md">
                    <div className="mb-8 flex justify-center lg:hidden">
                        <img src={AppImages?.bigLogo} alt="Mukizh Fashions" className="h-12 object-contain" />
                    </div>

                    <div className="rounded-2xl border border-border/60 bg-card/70 p-8 shadow-xl backdrop-blur-xl sm:p-10">
                        <h1 className="text-2xl font-semibold text-foreground">Welcome back</h1>
                        <p className="mt-1.5 text-sm text-muted-foreground">
                            Sign in to your Mukizh account to continue.
                        </p>

                        <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                            <div>
                                <div className="relative">
                                    <Input
                                        id="login-email"
                                        placeholder=" "
                                        autoComplete="off"
                                        onBlur={() => setTouched((t) => ({ ...t, userName: true }))}
                                        onChange={(e: any) => setApparelLoginValue({ ...apparelLoginValue, userName: e?.target?.value })}
                                        className="peer h-14 rounded-xl border-input/70 bg-background/60 px-4 pt-4 text-sm backdrop-blur placeholder:text-transparent focus-visible:ring-offset-0"
                                    />
                                    <label
                                        htmlFor="login-email"
                                        className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-muted-foreground transition-all peer-focus:top-2.5 peer-focus:translate-y-0 peer-focus:text-xs peer-focus:font-medium peer-focus:text-primary peer-[:not(:placeholder-shown)]:top-2.5 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-xs"
                                    >
                                        {AppConstant?.emailId}
                                    </label>
                                </div>
                                {touched.userName && emailInvalid && (
                                    <p className="mt-1.5 text-xs text-destructive">E-mail is required</p>
                                )}
                            </div>

                            <div>
                                <div className="relative">
                                    <Input
                                        id="login-password"
                                        placeholder=" "
                                        type={apparelLoginValue?.showPassword ? "text" : "password"}
                                        onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                                        onChange={(e: any) => setApparelLoginValue({ ...apparelLoginValue, password: e?.target?.value })}
                                        className="peer h-14 rounded-xl border-input/70 bg-background/60 px-4 pr-12 pt-4 text-sm backdrop-blur placeholder:text-transparent focus-visible:ring-offset-0"
                                    />
                                    <label
                                        htmlFor="login-password"
                                        className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-muted-foreground transition-all peer-focus:top-2.5 peer-focus:translate-y-0 peer-focus:text-xs peer-focus:font-medium peer-focus:text-primary peer-[:not(:placeholder-shown)]:top-2.5 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-xs"
                                    >
                                        {AppConstant?.password}
                                    </label>
                                    <button
                                        type="button"
                                        onClick={handleClickShowPassword}
                                        aria-label={apparelLoginValue?.showPassword ? "Hide password" : "Show password"}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                                    >
                                        {apparelLoginValue?.showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                                    </button>
                                </div>
                                {touched.password && passwordInvalid && (
                                    <p className="mt-1.5 text-xs text-destructive">Password is required</p>
                                )}
                            </div>

                            {showMessage == true && (
                                <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive">
                                    <AlertCircle className="h-4 w-4 shrink-0" /> Invalid email or password
                                </div>
                            )}

                            <Button type="submit" className="h-12 w-full rounded-xl text-sm font-medium">
                                {AppConstant?.login}
                            </Button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ApparelLoginPasswordFlow
*/
